import { css, Interpolation, SerializedStyles, Theme } from '@emotion/react'
import { memo } from 'react'
import { palette } from '../styles/colors'
import { pulsate, squish } from '../styles/keyframes'

const dotLoadingStyles = css({
    perspective: '1000px',
    display: 'inline-flex',
    alignSelf: 'center',
    flexFlow: 'row nowrap',
    paddingLeft: '4px',
    paddingRight: '4px',
})

const dotStyles = css({
    borderRadius: '50%',
    minWidth: '.5rem',
    minHeight: '.5rem',
    height: '0',
    animationDuration: '1s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease',
    animationName: squish,
    transformStyle: 'preserve-3d' as const,
    margin: '2px',
})

const firstDot = {
    animationDelay: '0s',
}
const secondDot = {
    animationDelay: '.33s',
}
const thirdDot = {
    animationDelay: '.66s',
}

interface InlineDotLoadingProps {
    styleOverrides?: Interpolation<Theme>
    active?: boolean
}

export const InlineDotLoading = memo((props: InlineDotLoadingProps) => {
    const { styleOverrides, active = false } = props
    if (!active) return null

    return (
        <div css={[dotLoadingStyles, styleOverrides]}>
            <span css={dotStyles} style={firstDot} />
            <span css={dotStyles} style={secondDot} />
            <span css={dotStyles} style={thirdDot} />
        </div>
    )
})

const pulseLoadingStyles = css({
    display: 'inline-flex',
    alignSelf: 'center',
    flexFlow: 'row nowrap',
    paddingLeft: '4px',
    paddingRight: '4px',
})

const pulseDotStyles = css({
    borderRadius: '50%',
    minWidth: '.5em',
    minHeight: '.5em',
    height: '.5em',
    width: '.5em',
    boxShadow: '0 0 0 0 rgba(56, 142, 60, 1)',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    animationName: pulsate,
    margin: '2px',
})

interface InlinePulseLoadingProps {
    active?: boolean
    styleOverrides?: any
}

export const InlinePulseLoading = memo((props: InlinePulseLoadingProps) => {
    const { active = false, styleOverrides } = props
    if (!active) return null

    return (
        <div css={pulseLoadingStyles} style={styleOverrides}>
            <span css={pulseDotStyles} />
        </div>
    )
})

const circularLoadingStyles = css`
    margin-top: 1px;
    display: flex;
    margin-bottom: 8px;
    .spinner {
        display: inline-block;
        position: relative;
        width: 5px;
        height: 5px;
    }
    .spinner div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: 14px;
        height: 14px;
        border: 3px solid ${palette.greenMFC};
        border-radius: 50%;
        animation: spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: ${palette.greenMFC} transparent transparent transparent;
    }
    .spinner div:nth-of-type(1) {
        animation-delay: -0.45s;
    }
    .spinner div:nth-of-type(2) {
        animation-delay: -0.3s;
    }
    .spinner div:nth-of-type(3) {
        animation-delay: -0.15s;
    }
    @keyframes spinner {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`

/**
 * Alternative to MUI's CircularLoading
 */
export const InlineCircularLoading = memo(
    ({ styleOverrides }: { styleOverrides?: SerializedStyles }) => {
        return (
            <div
                data-testid="InlineCircularLoading__Spinner"
                css={[circularLoadingStyles, styleOverrides]}
            >
                <div className="spinner">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>
        )
    },
)
